import type { $Fetch, NitroFetchOptions, NitroFetchRequest } from 'nitropack';
import { COOKIE_SESSION } from '#shared/const/cookies';
import { stores } from '~/store';

declare module '#app' {
  interface NuxtApp {
    $authApi: WrappedFetch;
  }
}

const ENetworkStatusMessage = {
  ECONNABORTED: 'ECONNABORTED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  ACCESS_RESTRICTED: 'ACCESS_RESTRICTED',
} as const;

export class WrappedFetch {
  readonly _fetch: $Fetch<unknown, NitroFetchRequest>;
  get fetch() {
    return this._fetch;
  }

  constructor(baseURL: string) {
    const runtimeConfig = useRuntimeConfig();

    this._fetch = $fetch.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
      onRequest(data) {
        const options = data.options;

        options.headers.set('MQTECH_REQUEST_KEY', runtimeConfig.public.techRequestKey);

        const session = useCookie(COOKIE_SESSION);

        if (session.value) {
          options.headers.set('session', session.value);
        }
      },
      onResponse() {
        // console.log({ response });
      },
      async onResponseError({ response = {} }) {
        const statusCode = response?.status;
        const statusMessage = response._data?.message?.toUpperCase();
        const errorText = response._data?.error;

        if (response.statusText === ENetworkStatusMessage.ECONNABORTED) {
          await stores.modals.showNetworkErrorModal('Network error', 'Something went wrong. Please try again');
        } else if (statusCode === 401 && statusMessage === ENetworkStatusMessage.UNAUTHORIZED) {
          stores.signUp.handleSignOut();
          stores.modals.showNetworkErrorModal('Access forbidden', errorText);
        } else if (statusCode === 403 && statusMessage === ENetworkStatusMessage.SESSION_EXPIRED) {
          stores.signUp.handleSignOut();
          stores.modals.showNetworkErrorModal('Logged Out', 'You have been logged out due to login on another device.');
        } else if (statusCode === 403 && statusMessage === ENetworkStatusMessage.ACCESS_RESTRICTED) {
          stores.signUp.handleSignOut();
          stores.modals.showNetworkErrorModal('Access forbidden', 'You are accessing from restricted country/state');
        } else {
          // Handle the rest of the errors
        }

        // console.log(response);
        // if (response.status === 401) {
        //   await nuxtApp.runWithContext(() => navigateTo('/'));
        // }
      },
    });
  }

  get<T = any>(
    request: NitroFetchRequest,
    options?: Omit<NitroFetchOptions<NitroFetchRequest, 'get'>, 'method'> | undefined,
  ): Promise<T> {
    return this._fetch(request, { ...options, method: 'GET' }) as unknown as Promise<T>;
  }

  post<T = any>(
    request: NitroFetchRequest,
    data?: any,
    options?: Omit<NitroFetchOptions<NitroFetchRequest, 'post'>, 'method' | 'body'> | undefined,
  ): Promise<T> {
    return this._fetch(request, { ...options, method: 'POST', body: data }) as unknown as Promise<T>;
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const authApi = new WrappedFetch(nuxtApp.$config.public.apiAuthUrl);

  return {
    provide: { authApi },
  };
});
